import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Descriptions, Input, InputNumber, Select, Space, Spin, Tag } from "antd";
import { ReloadOutlined } from "@ant-design/icons";

import salesRevenueApi from "../../../api/salesRevenueApi";

const RECEIVABLE_STATUS_TEXT = {
    PENDING_INVOICE: "待开票",
    INVOICED: "已开票",
    PARTIALLY_PAID: "部分收款",
    PAID: "已收款",
    CANCELLED: "已取消",
    EXCEPTION: "匹配异常",
};

const CODE_TEXT = {
    SALES_BOOKING_NOT_AD_HOC: "该订单未绑定一班一议合同",
    SALES_AD_HOC_AMOUNT_NOT_FOUND: "请先保存燃油手填再生成应收",
    SALES_AD_HOC_ALREADY_GENERATED: "该订单已生成燃油应收；如需修改请先撤销应收或走「调整金额」",
    CHARTER_BOOKING_NOT_FOUND: "找不到该订单",
};

function extractError(error, fallback = "操作失败") {
    const detail = error?.response?.data?.detail;
    if (typeof detail === "string") {
        return CODE_TEXT[detail] || detail || fallback;
    }
    if (detail && typeof detail === "object") {
        const code = detail.detail;
        let message = CODE_TEXT[code] || code || fallback;
        if (Array.isArray(detail.receivable_nos) && detail.receivable_nos.length) {
            message += `（${detail.receivable_nos.join("、")}）`;
        }
        return message;
    }
    return error?.message || fallback;
}

function toNumber(value) {
    if (value === null || value === undefined || value === "") return null;
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
}

function formatAmount(value) {
    const num = toNumber(value);
    if (num === null) return "—";
    return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * AD_HOC「一班一议手填燃油」面板：基础运价按合同航线价生成，这里只选手填燃油。
 */
export default function AdHocAmountPanel({ bookingId, canWrite, currencyOptions, messageApi, modalApi, onGenerated }) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState({ fuel_amount: null, currency: null, remark: null });
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);

    const load = useCallback(async () => {
        if (!bookingId) return;
        setLoading(true);
        try {
            const response = await salesRevenueApi.getBookingAdHocAmount(bookingId);
            const payload = response.data || {};
            setData(payload);
            setDraft({
                fuel_amount: toNumber(payload.fuel_amount),
                currency: payload.currency || payload.contract_currency || "USD",
                remark: payload.remark || null,
            });
        } catch (error) {
            messageApi.error(extractError(error, "获取一班一议燃油手填失败"));
        } finally {
            setLoading(false);
        }
    }, [bookingId, messageApi]);

    useEffect(() => {
        load();
    }, [load]);

    const readOnly = Boolean(data?.is_generated);
    const contractCurrency = data?.contract_currency || null;
    const currencyMismatch = Boolean(draft.currency && contractCurrency && draft.currency !== contractCurrency);

    const buildPayload = () => ({
        fuel_amount: draft.fuel_amount ?? null,
        currency: draft.currency || contractCurrency || "USD",
        remark: draft.remark?.trim() || null,
    });

    const handleSave = async () => {
        setSaving(true);
        try {
            await salesRevenueApi.saveBookingAdHocAmount(bookingId, buildPayload());
            messageApi.success("燃油手填草稿已保存");
            await load();
        } catch (error) {
            messageApi.error(extractError(error, "保存燃油手填失败"));
        } finally {
            setSaving(false);
        }
    };

    const runGenerate = async () => {
        setGenerating(true);
        try {
            await salesRevenueApi.saveBookingAdHocAmount(bookingId, buildPayload());
            const response = await salesRevenueApi.generateBookingAdHocReceivables(bookingId);
            const created = response.data?.created_count ?? 0;
            messageApi.success(created ? `已生成 ${created} 条燃油应收` : "燃油留空，未生成燃油应收");
            await load();
            if (onGenerated) await onGenerated();
        } catch (error) {
            messageApi.error(extractError(error, "生成燃油应收失败"));
        } finally {
            setGenerating(false);
        }
    };

    const handleGenerate = () => {
        if (toNumber(draft.fuel_amount) === null) {
            messageApi.warning("请先填写燃油附加费。不想收燃油就留空，不必点生成。");
            return;
        }
        modalApi.confirm({
            title: "确认生成燃油应收",
            content: (
                <div>
                    <p style={{ margin: "4px 0" }}>
                        燃油附加费：<strong>{formatAmount(draft.fuel_amount)} {draft.currency}</strong>
                    </p>
                    <p style={{ margin: "4px 0", color: "#999" }}>
                        基础运价仍按合同航线价生成。生成后本区块转只读，如需修改走「调整金额」或撤销应收。
                    </p>
                </div>
            ),
            okText: "生成燃油应收",
            cancelText: "取消",
            onOk: runGenerate,
        });
    };

    return (
        <div className="sales-revenue-card compact">
            <div className="sales-revenue-card-header">
                <span>一班一议手填燃油</span>
                <Space>
                    <Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={load}>
                        刷新
                    </Button>
                </Space>
            </div>
            <div className="sales-revenue-card-body">
                <Spin spinning={loading}>
                    {readOnly ? (
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message="燃油应收已生成，手填转为只读"
                            description="如需修改金额，请在应收明细里走「调整金额」；如需重填，请先撤销对应燃油应收。"
                        />
                    ) : (
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message="基础运价按合同航线价生成。本合同不算燃油公式，这班另外谈了燃油再手填。"
                            description="燃油附加费可留空（留空则不生成燃油应收）、可为负数（减免）。"
                        />
                    )}

                    {(data?.warnings || []).map((warning) => (
                        <Alert key={warning} type="warning" showIcon style={{ marginBottom: 8 }} message={warning} />
                    ))}
                    {!readOnly && currencyMismatch ? (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 8 }}
                            message={`手填币种 ${draft.currency} 与合同币种 ${contractCurrency} 不一致，下游发票/收款请留意`}
                        />
                    ) : null}

                    {readOnly ? (
                        <>
                            <Descriptions
                                bordered
                                size="small"
                                column={{ xxl: 2, xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
                                labelStyle={{ width: 110, whiteSpace: "nowrap" }}
                                items={[
                                    {
                                        key: "fuel",
                                        label: "燃油附加费",
                                        children: (
                                            <span className="sales-revenue-money">
                                                {data?.fuel_amount == null ? "未填（未生成）" : `${formatAmount(data.fuel_amount)} ${data?.currency || ""}`}
                                            </span>
                                        ),
                                    },
                                    { key: "currency", label: "币种", children: data?.currency || "—" },
                                    { key: "remark", label: "备注", children: data?.remark || "—" },
                                ]}
                            />
                            <div style={{ marginTop: 12 }}>
                                <Space size={8} wrap>
                                    {(data?.receivables || []).map((item) => (
                                        <Tag key={item.receivable_id} color={item.status === "PENDING_INVOICE" ? "blue" : "green"}>
                                            燃油附加费 {formatAmount(item.amount)} {item.currency} ·{" "}
                                            {RECEIVABLE_STATUS_TEXT[item.status] || item.status}
                                        </Tag>
                                    ))}
                                </Space>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="sales-revenue-form-grid two-columns">
                                <div>
                                    <div style={{ marginBottom: 4 }}>燃油附加费（可留空 / 可为负）</div>
                                    <InputNumber
                                        precision={2}
                                        style={{ width: "100%" }}
                                        disabled={!canWrite}
                                        value={draft.fuel_amount}
                                        onChange={(value) => setDraft((prev) => ({ ...prev, fuel_amount: value }))}
                                        placeholder="留空 = 不生成燃油应收"
                                    />
                                </div>
                                <div>
                                    <div style={{ marginBottom: 4 }}>币种（默认继承合同）</div>
                                    <Select
                                        style={{ width: "100%" }}
                                        disabled={!canWrite}
                                        value={draft.currency}
                                        options={currencyOptions}
                                        onChange={(value) => setDraft((prev) => ({ ...prev, currency: value }))}
                                    />
                                </div>
                                <div>
                                    <div style={{ marginBottom: 4 }}>备注</div>
                                    <Input
                                        disabled={!canWrite}
                                        value={draft.remark || ""}
                                        onChange={(event) => setDraft((prev) => ({ ...prev, remark: event.target.value }))}
                                        placeholder="如 与客户 2026-07 邮件议价"
                                    />
                                </div>
                            </div>
                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                                {canWrite ? (
                                    <Space>
                                        <Button loading={saving} onClick={handleSave}>
                                            保存草稿
                                        </Button>
                                        <Button type="primary" loading={generating} onClick={handleGenerate}>
                                            生成燃油应收
                                        </Button>
                                    </Space>
                                ) : null}
                            </div>
                        </>
                    )}
                </Spin>
            </div>
        </div>
    );
}
